import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../css/Navbar.css';
// ChangeAnoBaseModal removed — year control is inline in the navbar
import PdfConfirmModal from './modals/PdfConfirmModal';
import MesSelectionModal from './modals/MesSelectionModal';
import PeriodoSelectionModal from './modals/PeriodoSelectionModal';
import ProgramaPeriodoModal from './modals/ProgramaPeriodoModal';
import PdfViewerModal from './modals/PdfViewerModal';
import DefaultModal from './modals/DefaultModal';
import ChangePasswordModal from './modals/ChangePasswordModal';
import { logout } from '../api/logout';
import {
  relatoriosGlobais,
  gerarRelatorioComProgramaPeriodo,
} from '../utils/relatoriosGlobais';
import usePdfConfirmModal from '../hooks/usePdfConfirmModal';
import useMesSelectionModal from '../hooks/useMesSelectionModal';
import usePeriodoSelectionModal from '../hooks/usePeriodoSelectionModal';
import useProgramaPeriodoModal from '../hooks/useProgramaPeriodoModal';
import useDefaultModal from './hooks/useDefaultModal';
import { carregarPermissoes } from '../utils/permissions';

function Navbar() {
  const [user, setUser] = useState(null);
  const [associacao, setAssociacao] = useState(null);
  const [usuarioBind, setUsuarioBind] = useState(null);
  const [anoBase, setAnoBase] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  // modal removed: use inline controls to change year
  const [permissoes, setPermissoes] = useState({});
  const [permissoesCarregadas, setPermissoesCarregadas] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const storedOptions = localStorage.getItem('relAssociacaoUsuario');
  const parsedOptions = JSON.parse(storedOptions);
  const { setDefaultModal, showDefaultModal } = useDefaultModal();

  // Modais de filtro
  const {
    data: pdfModalData,
    show: showPdfModal,
    open: openPdfModal,
    close: closePdfModal,
    confirm: handleConfirmPdf,
    isLoading: pdfModalLoading,
    setLoading: setPdfModalLoading,
  } = usePdfConfirmModal();

  const {
    data: mesModalData,
    show: showMesModal,
    open: openMesModal,
    close: closeMesModal,
    confirm: handleConfirmMes,
    isLoading: mesModalLoading,
    setLoading: setMesModalLoading,
  } = useMesSelectionModal();

  const {
    data: periodoModalData,
    show: showPeriodoModal,
    open: openPeriodoModal,
    close: closePeriodoModal,
    confirm: handleConfirmPeriodo,
    isLoading: periodoModalLoading,
    setLoading: setPeriodoModalLoading,
  } = usePeriodoSelectionModal();

  const {
    data: programaPeriodoData,
    show: showProgramaPeriodoModal,
    open: openProgramaPeriodoModal,
    close: closeProgramaPeriodoModal,
    confirm: handleConfirmProgramaPeriodo,
    isLoading: programaPeriodoModalLoading,
    setLoading: setProgramaPeriodoModalLoading,
  } = useProgramaPeriodoModal();

  // Função para verificar se o sistema está aberto em múltiplas abas
  const checkIfMultipleTabsOpen = () => {
    // Usar a API de Document Visibility State
    if (document.hidden !== undefined) {
      // Armazenar um timestamp na sessionStorage para cada aba
      const currentTabId = sessionStorage.getItem('tabId') || 'tab-' + Date.now();
      if (!sessionStorage.getItem('tabId')) {
        sessionStorage.setItem('tabId', currentTabId);
      }

      // Contar quantas abas têm o mesmo localStorage setado
      // Usaremos uma chave única no localStorage para rastrear abas ativas
      const tabSessionKey = 'gfinanc_active_tabs';
      let activeTabs = JSON.parse(localStorage.getItem(tabSessionKey) || '{}');
      
      // Atualizar ou adicionar a aba atual
      activeTabs[currentTabId] = Date.now();
      
      // Limpar abas que não foram atualizadas nos últimos 5 segundos
      const now = Date.now();
      Object.keys(activeTabs).forEach(tabId => {
        if (now - activeTabs[tabId] > 5000) {
          delete activeTabs[tabId];
        }
      });
      
      localStorage.setItem(tabSessionKey, JSON.stringify(activeTabs));
      
      // Retornar verdadeiro se houver mais de uma aba ativa
      return Object.keys(activeTabs).length > 1;
    }
    return false;
  };

  const MIN_YEAR = 2023;
  const MAX_YEAR = new Date().getFullYear();

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  const getTabCount = () => {
    const tabSessionKey = 'gfinanc_active_tabs';
    const activeTabs = JSON.parse(localStorage.getItem(tabSessionKey) || '{}');
    return Object.keys(activeTabs).length;
  };

  const handleChangeAno = (delta) => {
    // Reusa verificação de múltiplas abas
    if (checkIfMultipleTabsOpen()) {
      showDefaultModal(
        'error',
        'O sistema está aberto em mais de uma aba do navegador. Feche as outras abas para trocar o ano base.'
      );
      return;
    }

    const current = parseInt(anoBase) || MAX_YEAR;
    const newYear = clamp(current + delta, MIN_YEAR, MAX_YEAR);

    // Aplicar mudança de ano imediatamente (inline control)
    localStorage.setItem('anoBase', String(newYear));
    setAnoBase(String(newYear));
    window.location.reload();
  };

  const handleLogout = () => {
    const token = localStorage.getItem('token');
    if (token) { logout(token).catch(() => {}); }
    // Limpa somente dados de sessão relacionados a login (não remover tabId)
    localStorage.clear();
    try { sessionStorage.setItem('justLoggedOut','true'); sessionStorage.removeItem('loginRedirectMessage'); } catch (e) { /* noop */ }
    navigate('/');
  };

  const openPdfWindow = (pdfUrl, title) => {
    localStorage.setItem('pdfViewerData', JSON.stringify({ url: pdfUrl, title }));
    // Disparar evento customizado para notificar o PdfViewer modal
    const event = new CustomEvent('pdfViewerUpdate', { detail: { url: pdfUrl, title } });
    window.dispatchEvent(event);
  };

  const handleRelatorioWithoutFilter = async () => {
    setPdfModalLoading(true);
    try {
      if (pdfModalData && pdfModalData.action) {
        await pdfModalData.action(openPdfWindow);
      }
      handleConfirmPdf();
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      alert('Erro ao gerar relatório: ' + error.message);
    } finally {
      setPdfModalLoading(false);
    }
  };

  const handleRelatorioMes = async (mes) => {
    setMesModalLoading(true);
    try {
      if (mesModalData && mesModalData.action) {
        await mesModalData.action(mes, openPdfWindow);
      }
      handleConfirmMes();
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      alert('Erro ao gerar relatório: ' + error.message);
    } finally {
      setMesModalLoading(false);
    }
  };

  const handleRelatorioPeriodo = async (dataInicio, dataFim) => {
    setPeriodoModalLoading(true);
    try {
      if (periodoModalData && periodoModalData.action) {
        await periodoModalData.action(dataInicio, dataFim, openPdfWindow);
      }
      handleConfirmPeriodo();
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      alert('Erro ao gerar relatório: ' + error.message);
    } finally {
      setPeriodoModalLoading(false);
    }
  };

  const handleRelatorioProgramaPeriodo = async (filtros) => {
    setProgramaPeriodoModalLoading(true);
    try {
      if (programaPeriodoData && programaPeriodoData.action) {
        // O action contém o endpoint
        const endpoint = typeof programaPeriodoData.action === 'string' 
          ? programaPeriodoData.action 
          : 'despesa_programa_periodo';
        
        // Merging filtros recebido do modal com os dados do programa (label)
        const dadosFiltro = filtros || programaPeriodoData;
        
        await gerarRelatorioComProgramaPeriodo(
          endpoint,
          dadosFiltro,
          programaPeriodoData.label,
          openPdfWindow
        );
      }
      handleConfirmProgramaPeriodo();
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      alert('Erro ao gerar relatório: ' + error.message);
    } finally {
      setProgramaPeriodoModalLoading(false);
    }
  };

  // Detectar múltiplas abas usando BroadcastChannel
  // (gerenciado pelo index.js antes do render)

  useEffect(() => {
    if (localStorage.getItem('token') === null) {
      navigate('/');
    }
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    } else {
      navigate('/');
    }
    const associacaoData = localStorage.getItem('associacao');
    if (associacaoData) {
      setAssociacao(JSON.parse(associacaoData));
    }
    const usuarioBindData = localStorage.getItem('usuarioBind');
    if (usuarioBindData) {
      setUsuarioBind(JSON.parse(usuarioBindData));
    }
    const anoBaseData = localStorage.getItem('anoBase');
    if (anoBaseData) {
      setAnoBase(anoBaseData);
    }

    // Inicializar tabId se não existir
    if (!sessionStorage.getItem('tabId')) {
      sessionStorage.setItem('tabId', 'tab-' + Date.now());
    }

    // Configurar intervalo para atualizar o timestamp da aba ativa
    const tabUpdateInterval = setInterval(() => {
      const tabSessionKey = 'gfinanc_active_tabs';
      const tabId = sessionStorage.getItem('tabId');
      let activeTabs = JSON.parse(localStorage.getItem(tabSessionKey) || '{}');
      
      activeTabs[tabId] = Date.now();
      localStorage.setItem(tabSessionKey, JSON.stringify(activeTabs));
    }, 2000); // Atualizar a cada 2 segundos

    return () => clearInterval(tabUpdateInterval);
  }, [navigate]);

  // UseEffect para carregar permissões do usuário
  useEffect(() => {
    const carregarPermissoesUsuario = async () => {
      if (user?.cpf && associacao?.cnpj) {
        try {
          const perms = await carregarPermissoes(user.cpf, associacao.cnpj);
          setPermissoes(perms || {});
          setPermissoesCarregadas(true);
        } catch (error) {
          console.error('Erro ao carregar permissões no Navbar:', error);
          setPermissoesCarregadas(true); // Marca como carregado mesmo com erro para não bloquear UI
        }
      }
    };
    
    carregarPermissoesUsuario();
  }, [user, associacao]);

  // Função helper para verificar se pode abrir um módulo
  const podeAbrirModulo = (modulo) => {
    // Enquanto permissões não foram carregadas, permite acesso (evita flicker)
    if (!permissoesCarregadas) {
      return true;
    }
    
    // Se não há permissão configurada para o módulo, permite por padrão
    if (!permissoes[modulo]) {
      return true;
    }
    
    // Verifica a permissão de 'abrir'
    return permissoes[modulo].abrir !== false;
  };

  // UseEffect para verificar se deve abrir o modal de ano base após redirecionamento
  useEffect(() => {
    const shouldOpen = localStorage.getItem('shouldOpenAnoBaseModal');
    if (shouldOpen === 'true' && location.pathname === '/home') {
      // Remover a flag e mostrar informação ao usuário (modal removido)
      localStorage.removeItem('shouldOpenAnoBaseModal');
      setTimeout(() => {
        showDefaultModal('info', 'Use os controles de ano na barra superior para alterar o Ano Base.');
      }, 300);
    }
  }, [location.pathname]);

  // pendingAnoBase removed — year changes are applied immediately via inline control

  // UseEffect para configurar o comportamento do submenu
  useEffect(() => {
    const handleSubmenu = () => {
      const submenuToggle = document.getElementById('planejamentoDropdown');

      if (submenuToggle) {
        submenuToggle.addEventListener('click', function (e) {
          const isMobile = window.innerWidth < 992;

          if (isMobile) {
            e.preventDefault();
            e.stopPropagation();

            const submenu = this.nextElementSibling;
            if (submenu) {
              const isCurrentlyVisible = submenu.classList.contains('show');

              // Fechar outros submenus
              document.querySelectorAll('.dropdown-menu.show').forEach(menu => {
                if (menu !== submenu) {
                  menu.classList.remove('show');
                }
              });

              // Toggle do submenu atual
              if (isCurrentlyVisible) {
                submenu.classList.remove('show');
              } else {
                submenu.classList.add('show');
              }
            }
          }
        });
      }
    };

    // Aguarda o DOM carregar completamente
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', handleSubmenu);
    } else {
      handleSubmenu();
    }

    return () => {
      document.removeEventListener('DOMContentLoaded', handleSubmenu);
    };
  }, []);

  if (!user) {
    return <div>Loading...</div>;
  }

  const isActive = path =>
    location.pathname === path ? 'nav-link active px-2' : 'nav-link px-2';

  return (
    <nav
      className="navbar navbar-expand-lg navbar-dark fixed-top"
      style={{
        background:
          'linear-gradient(0deg, rgb(7, 99, 7) 0%, rgb(7, 59, 43) 100%)',
      }}
    >
      <div className="container-fluid">
        {/* Logo */}
        <a href="/home" className="d-flex align-items-center navbar-brand">
          <img
            src="/assets/logo.png"
            alt="Logo"
            width="80"
            height="40"
            className="d-inline-block align-top me-2 me-lg-3 rounded-3"
          />
        </a>

        {/* Botão Toggler para Mobile */}
        <button
          className="navbar-toggler border-white"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarContent"
          aria-controls="navbarContent"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <i className="bi bi-list text-white"></i>
        </button>

        {/* Conteúdo da Navbar */}
        <div className="collapse navbar-collapse" id="navbarContent">
          {/* Seção Esquerda - Links de Navegação */}
          <ul className="navbar-nav me-auto mb-3 mb-lg-0">
            <li className="nav-item">
              <a className={isActive('/home')} href="/home">
                <i className="bi bi-house-door me-1 d-lg-none"></i>
                Home
              </a>
            </li>
            {usuarioBind?.tipo === 'ADMIN' && user?.role === 'ADMIN' && (
              <li className="nav-item dropdown">
                <a
                  className={
                    location.pathname.startsWith('/associacoes') ||
                    location.pathname.startsWith('/usuarios') ||
                    location.pathname.startsWith('/eas_cadastrados') ||
                    location.pathname.startsWith('/beneficiario_cadastrados') ||
                    location.pathname.startsWith('/templates') ||
                    location.pathname.startsWith('/elemento_despesas') ||
                    location.pathname.startsWith('/fonte_recursos')
                      ? 'nav-link active px-2 dropdown-toggle'
                      : 'nav-link px-2 dropdown-toggle'
                  }
                  href="#"
                  id="adminDropdown"
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <i className="bi bi-gear me-1 d-lg-none"></i>
                  Administração
                </a>
                <ul
                  className="dropdown-menu text-white border border-secondary"
                  style={{
                    background:
                      'linear-gradient(0deg, rgb(7, 99, 7) 0%, rgb(7, 59, 43) 100%)',
                  }}
                  aria-labelledby="adminDropdown"
                >
                  {podeAbrirModulo('associacoes') && (
                    <li>
                      <a className="dropdown-item text-white" href="/associacoes">
                        <i className="bi bi-building me-2"></i>
                        Associações
                      </a>
                    </li>
                  )}
                  {podeAbrirModulo('usuarios') && (
                    <li>
                      <a className="dropdown-item text-white" href="/usuarios">
                        <i className="bi bi-people me-2"></i>
                        Usuários
                      </a>
                    </li>
                  )}

                  <li>
                    <a className="dropdown-item text-white" href="/templates">
                      <i className="bi bi-file me-2"></i>
                      Templates
                    </a>
                  </li>
                  <li>
                    <hr className="dropdown-divider bg-white" />
                  </li>
                  {podeAbrirModulo('elemento_despesa') && (
                    <li>
                      <a
                        className="dropdown-item text-white"
                        href="/elemento_despesas"
                      >
                        <i className="bi bi-receipt me-2"></i>
                        Elementos de Despesa
                      </a>
                    </li>
                  )}
                  {podeAbrirModulo('fontes_recurso') && (
                    <li>
                      <a
                        className="dropdown-item text-white"
                        href="/fonte_recursos"
                      >
                        <i className="bi bi-cash-stack me-2"></i>
                        Fonte de Recursos
                      </a>
                    </li>
                  )}
                  <li>
                    <hr className="dropdown-divider bg-white" />
                  </li>
                  <li>
                    <a className="dropdown-item text-white" href="/sessions-ativas">
                      <i className="bi bi-activity me-2"></i>
                      Sessões Ativas
                    </a>
                  </li>
                </ul>
              </li>
            )}

              

            
            {/* Menu Cadastro no nível principal */}
            {((usuarioBind?.tipo === 'ADMIN' && user?.role === 'ADMIN') || (usuarioBind?.tipo === 'USER' && user?.role === 'USER')) && (
              <li className="nav-item dropdown">
                <a
                  className={
                    location.pathname.startsWith('/imagens') ||
                    location.pathname.startsWith('/programas') ||
                    location.pathname.startsWith('/acoes') ||
                    location.pathname.startsWith('/sub_acoes') ||
                    location.pathname.startsWith('/itens') ||
                    location.pathname.startsWith('/contas') ||
                    location.pathname.startsWith('/orcamentos') ||
                    location.pathname.startsWith('/favorecidos') ||
                    location.pathname.startsWith('/pessoas') ||
                    location.pathname.startsWith('/pessoa_dependentes') ||
                    location.pathname.startsWith('/pessoa') ||
                    location.pathname.startsWith('/pessoa_dependente')
                      ? 'nav-link active px-2 dropdown-toggle'
                      : 'nav-link px-2 dropdown-toggle'
                  }
                  href="#"
                  id="cadastroDropdown"
                  role="button"
                  data-bs-toggle="dropdown"
                  data-bs-auto-close="outside"
                  aria-expanded="false"
                >
                  <i className="bi bi-folder-plus me-1 d-lg-none"></i>
                  Cadastros
                </a>
                <ul
                  className="dropdown-menu text-white border border-secondary"
                  style={{
                    background:
                      'linear-gradient(0deg, rgb(7, 99, 7) 0%, rgb(7, 59, 43) 100%)',
                  }}
                  aria-labelledby="cadastroDropdown"
                >
                  {podeAbrirModulo('pessoas') && (
                    <li>
                      <a className="dropdown-item text-white" href="/pessoas">
                        <i className="bi bi-people me-2"></i>
                        Associados
                      </a>
                    </li>
                  )}
                  {usuarioBind?.tipo === 'ADMIN' && user?.role === 'ADMIN' && (
                    <>
                      <li className="dropdown">
                        <a
                          className="dropdown-item text-white dropdown-toggle"
                          href="#"
                          id="planejamentoDropdown"
                          role="button"
                          data-bs-toggle="dropdown"
                          data-bs-auto-close="outside"
                          aria-expanded="false"
                        >
                          <i className="bi bi-diagram-3 me-2"></i>
                          Planejamento
                        </a>
                        <ul
                          className="dropdown-menu text-white border border-secondary"
                          style={{
                            background:
                              'linear-gradient(0deg, rgb(7, 99, 7) 0%, rgb(7, 59, 43) 100%)',
                          }}
                          aria-labelledby="planejamentoDropdown"
                        >
                          {podeAbrirModulo('programas') && (
                            <li>
                              <a
                                className="dropdown-item text-white"
                                href="/programas"
                              >
                                <i className="bi bi-list-task me-2"></i>
                                Programas
                              </a>
                            </li>
                          )}
                          {podeAbrirModulo('acoes') && (
                            <li>
                              <a className="dropdown-item text-white" href="/acoes">
                                <i className="bi bi-gear me-2"></i>
                                Ações
                              </a>
                            </li>
                          )}
                          {podeAbrirModulo('subacoes') && (
                            <li>
                              <a
                                className="dropdown-item text-white"
                                href="/sub_acoes"
                              >
                                <i className="bi bi-gear-fill me-2"></i>
                                Sub-Ações
                              </a>
                            </li>
                          )}
                          <li>
                            <a className="dropdown-item text-white" href="/itens">
                              <i className="bi bi-box me-2"></i>
                              Itens
                            </a>
                          </li>
                        </ul>
                      </li>
                      {podeAbrirModulo('orcamentos') && (
                        <li>
                          <a className="dropdown-item text-white" href="/orcamentos">
                            <i className="bi bi-cash-coin me-2"></i>
                            Orçamentos
                          </a>
                        </li>
                      )}
                    </>
                  )}
                  {podeAbrirModulo('favorecidos') && (
                    <li>
                      <a className="dropdown-item text-white" href="/favorecidos">
                        <i className="bi bi-building me-2"></i>
                        Favorecidos
                      </a>
                    </li>
                  )}
                  {podeAbrirModulo('contas') && (
                    <li>
                      <a className="dropdown-item text-white" href="/contas">
                        <i className="bi bi-cash-stack me-2"></i>
                        Contas
                      </a>
                    </li>
                  )}
                  {usuarioBind?.tipo === 'ADMIN' && user?.role === 'ADMIN' && podeAbrirModulo('bancos') && (
                    <>
                      <li>
                        <hr className="dropdown-divider bg-white" />
                      </li>
                      <li>
                        <a className="dropdown-item text-white" href="/bancos">
                          <i className="bi bi-images me-2"></i>
                          Bancos
                        </a>
                      </li>
                    </>
                  )}
                    {usuarioBind?.tipo === 'ADMIN' && user?.role === 'ADMIN' && (
                    <>
                      <li>
                        <hr className="dropdown-divider bg-white" />
                      </li>
                      <li>
                        <a className="dropdown-item text-white" href="/imagens">
                          <i className="bi bi-images me-2"></i>
                          Imagens
                        </a>
                      </li>
                    </>
                  )}
                </ul>
              </li>
            )}
            <li className="nav-item dropdown">
              <a
                className={
                  location.pathname.startsWith('/despesas') ||
                  location.pathname.startsWith('/rendimentos') ||
                  location.pathname.startsWith('/saldos') ||
                  location.pathname.startsWith('/saldos-investimento') ||
                  location.pathname.startsWith('/receitas') ||
                  location.pathname.startsWith('/redistribuicoes') ||
                  location.pathname.startsWith('/movimento-mensal') ||
                  location.pathname.startsWith('/execucao_orcamentaria')
                    ? 'nav-link active px-2 dropdown-toggle'
                    : 'nav-link px-2 dropdown-toggle'
                }
                href="#"
                id="movimentoDropdown"
                role="button"
                data-bs-toggle="dropdown"
                data-bs-auto-close="outside"
                aria-expanded="false"
              >
                <i className="bi bi-arrow-left-right me-1 d-lg-none"></i>
                Movimento
              </a>
              <ul
                className="dropdown-menu text-white border border-secondary"
                style={{
                  background:
                    'linear-gradient(0deg, rgb(7, 99, 7) 0%, rgb(7, 59, 43) 100%)',
                }}
                aria-labelledby="movimentoDropdown"
              >
                {podeAbrirModulo('despesas') && (
                  <li>
                    <a className="dropdown-item text-white" href="/despesas">
                      <i className="bi bi-graph-down-arrow me-2"></i>
                      Despesas
                    </a>
                  </li>
                )}
                {podeAbrirModulo('receitas') && (
                  <li>
                    <a className="dropdown-item text-white" href="/receitas">
                      <i className="bi bi-graph-up-arrow me-2"></i>
                      Receitas
                    </a>
                  </li>
                )}
                {podeAbrirModulo('rendimentos') && (
                  <li>
                    <a className="dropdown-item text-white" href="/rendimentos">
                      <i className="bi bi-graph-up me-2"></i>
                      Rendimentos
                    </a>
                  </li>
                )}
                {podeAbrirModulo('saldos') && (
                  <li>
                    <a className="dropdown-item text-white" href="/saldos">
                      <i className="bi bi-wallet2 me-2"></i>
                      Saldos
                    </a>
                  </li>
                )}
                {podeAbrirModulo('saldos_investimento') && (
                  <li>
                    <a className="dropdown-item text-white" href="/saldos-investimento">
                      <i className="bi bi-piggy-bank me-2"></i>
                      Saldos de Investimento
                    </a>
                  </li>
                )}
                {podeAbrirModulo('redistribuicoes') && (
                  <li>
                    <a
                      className="dropdown-item text-white"
                      href="/redistribuicoes"
                    >
                      <i className="bi bi-arrow-left-right me-2"></i>
                      Redistribuições
                    </a>
                  </li>
                )}
                <li>
                  <hr className="dropdown-divider bg-white" />
                </li>
                {podeAbrirModulo('execucao_orcamentaria') && (
                  <li>
                    <a className="dropdown-item text-white" href="/execucao_orcamentaria">
                      <i className="bi bi-graph-up me-2"></i>
                      Execução Orçamentária
                    </a>
                  </li>
                )}
                <li>
                  <a
                    className="dropdown-item text-white"
                    href="/movimento-mensal"
                  >
                    <i className="bi bi-calendar-month me-2"></i>
                    Movimento Mensal
                  </a>
                </li>
                <li>
                  <a
                    className="dropdown-item text-white"
                    href="/movimento-anual"
                  >
                    <i className="bi bi-calendar-range me-2"></i>
                    Movimento Anual
                  </a>
                </li>
                <li>
                  <a
                    className="dropdown-item text-white"
                    href="/conciliacao-bancaria"
                  >
                    <i className="bi bi-bank me-2"></i>
                    Conferência Financeira
                  </a>
                </li>
              </ul>
            </li>
            {podeAbrirModulo('monitoramento') && (
              <li className="nav-item">
                <a className={isActive('/monitoramento-interno')} href="/monitoramento-interno">
                  <i className="bi bi-clipboard-data me-1 d-lg-none"></i>
                  Monitoramento
                </a>
              </li>
            )}
            {/* <li className="nav-item">
              <a
                className={isActive('/saldo-orcamentario')}
                href="/saldo-orcamentario"
              >
                <i className="bi bi-calculator me-1 d-lg-none"></i>
                Saldo Orçamentário
              </a>
            </li> */}
            {/* <li className="nav-item">
              <a className={isActive('/pagamentos')} href="/pagamentos">
                <i className="bi bi-building me-1 d-lg-none"></i>
                Pagamentos
              </a>
            </li> */}

            {/* Relatórios globais */}
            {relatoriosGlobais.length > 0 && podeAbrirModulo('relatorios') && (
              <li className="nav-item dropdown">
                <a
                  className="nav-link px-2 dropdown-toggle"
                  href="#"
                  id="relatoriosDropdown"
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <i className="bi bi-file-earmark-pdf me-1 d-lg-none"></i>
                  Relatórios
                </a>
                <ul
                  className="dropdown-menu text-white border border-secondary"
                  style={{
                    background:
                      'linear-gradient(0deg, rgb(7, 99, 7) 0%, rgb(7, 59, 43) 100%)',
                  }}
                  aria-labelledby="relatoriosDropdown"
                >
                  {/* Relatórios globais sempre disponíveis */}
                  {relatoriosGlobais.map((relatorio, index) => (
                    <li key={`global-${index}`}>
                      <button
                        className="dropdown-item text-white"
                        onClick={() => {
                          if (relatorio.requiresMes) {
                            openMesModal(relatorio);
                          } else if (relatorio.requiresPeriodo) {
                            openPeriodoModal(relatorio);
                          } else if (relatorio.requiresProgramaPeriodo) {
                            openProgramaPeriodoModal(relatorio);
                          } else {
                            openPdfModal(relatorio);
                          }
                        }}
                        title={relatorio.tooltip}
                      >
                        <i className={`${relatorio.icon} me-2`}></i>
                        {relatorio.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </li>
            )}

            {/* <li className="nav-item">
                            <a className={isActive('/pessoas')} href="/pessoas">
                                <i className="bi bi-file-earmark-text me-1 d-lg-none"></i>
                                Pessoas
                            </a>
                        </li>
                        <li className="nav-item">
                            <a className={isActive('/ata')} href="/ata">
                                <i className="bi bi-file-earmark-text me-1 d-lg-none"></i>
                                Ata
                            </a>
                        </li> */}

            {/* Controle Ano Base (exibe após o menu Relatórios no desktop) */}
            <li className="nav-item d-none d-lg-flex align-items-center me-2">
              <div
                className="ano-control input-group input-group-sm"
                role="group"
                aria-label="Controle Ano Base"
                style={{ whiteSpace: 'nowrap', flexShrink: 0, gap: 0 }}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    handleChangeAno(-1);
                  } else if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    handleChangeAno(1);
                  }
                }}
              >
                <button
                  type="button"
                  className="input-group-text"
                  onClick={() => handleChangeAno(-1)}
                  aria-label="Ano anterior"
                  style={{ cursor: 'pointer', backgroundColor: 'rgb(7, 99, 7)', color: '#fff', borderColor: 'rgb(7, 99, 7)' }}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
                <input
                  type="text"
                  inputMode="numeric"
                  className="input-group-text text-center"
                  aria-label="Ano base"
                  readOnly
                  value={anoBase || MAX_YEAR}
                  style={{ width: '60px', backgroundColor: 'rgb(7, 99, 7)', color: '#fff', borderColor: 'rgb(7, 99, 7)' }}
                />
                <button
                  type="button"
                  className="input-group-text"
                  onClick={() => handleChangeAno(1)}
                  aria-label="Próximo ano"
                  style={{ cursor: 'pointer', backgroundColor: 'rgb(7, 99, 7)', color: '#fff', borderColor: 'rgb(7, 99, 7)' }}
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            </li>

          </ul>

          {/* Seção Direita - Usuário e Menu */}
          <div className="d-flex flex-column flex-lg-row align-items-stretch align-items-lg-center gap-3 ms-lg-auto">
            {/* Informações do Usuário (visível apenas em desktop) */}
            <div className="d-none d-lg-flex flex-column text-end">
              {user?.nome && (
                <span
                  className="text-white fw-semibold"
                  style={{ fontSize: '0.9em' }}
                >
                  {user.nome}
                </span>
              )}
              {associacao?.nome && (
                <span
                  className="text-white opacity-75"
                  style={{ fontSize: '0.7em' }}
                >
                  {/* {associacao.nome} */}
                  {associacao.nome}
                </span>
              )}
            </div>

            {/* Controle Ano Base: móvel no dropdown do usuário; o desktop será exibido junto aos menus (inserido no bloco de navegação) */}

            {/* Dropdown do Usuário */}
            <div className="nav-item dropdown">
              <button
                className="nav-link dropdown-toggle btn btn-link text-white d-flex align-items-center"
                id="userDropdown"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {/* Ícone e nome abreviado em mobile */}
                <div className="d-flex align-items-center">
                  <i className="bi bi-person-circle fs-5"></i>
                  <span className="ms-2 d-lg-none">
                    {user?.nome && user.nome.split(' ')[0]}
                  </span>
                </div>
              </button>
              <ul
                className="dropdown-menu dropdown-menu-end bg-success text-white border border-secondary mt-2"
                style={{
                  background:
                    'linear-gradient(0deg, rgb(7, 99, 7) 0%, rgb(7, 59, 43) 100%)',
                  minWidth: '220px',
                }}
              >
                <li>
                    <div className="d-block d-lg-none px-3 py-2">
                      {user?.nome && (
                        <div className="text-white fw-semibold">{user.nome}</div>
                      )}
                      {associacao?.nome && (
                        <div className="text-white opacity-75 small">
                          {associacao.nome}
                        </div>
                      )}

                      {/* Controle Ano Base (mobile) */}
                      <div className="mt-2 d-flex align-items-center gap-1">
                        <span className="text-white me-2" style={{ fontSize: '0.8em' }}>
                          Ano Base
                        </span>
                        <div
                          className="ano-control"
                          role="group"
                          aria-label="Controle Ano Base"
                          style={{ gap: '0.25rem' }}
                        >
                          <button
                            type="button"
                            className="input-group-text"
                            onClick={() => handleChangeAno(-1)}
                            aria-label="Ano anterior"
                            style={{ cursor: 'pointer', backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}
                          >
                            <i className="bi bi-chevron-left"></i>
                          </button>
                          <input
                            type="text"
                            inputMode="numeric"
                            className="input-group-text text-center"
                            readOnly
                            value={anoBase || MAX_YEAR}
                            style={{ width: '50px', backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}
                          />
                          <button
                            type="button"
                            className="input-group-text"
                            onClick={() => handleChangeAno(1)}
                            aria-label="Próximo ano"
                            style={{ cursor: 'pointer', backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}
                          >
                            <i className="bi bi-chevron-right"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                </li>
                <li>
                  <hr className="dropdown-divider bg-white mx-2 d-lg-none" />
                </li>
                {parsedOptions.length > 1 && (
                  <li>
                    <a
                      className="dropdown-item text-white"
                      href="/seleciona_entidade"
                    >
                      <i className="bi bi-arrow-left-right me-2"></i>
                      Trocar Associacao
                    </a>
                  </li>
                )}
                
                
                <li>
                  <button
                    className="dropdown-item text-white"
                    onClick={() => setShowChangePassword(true)}
                  >
                    <i className="bi bi-key me-2"></i>
                    Alterar senha
                  </button>
                </li>
                <li>
                  <hr className="dropdown-divider bg-white mx-2 d-lg-none" />
                </li>
                <li>
                  <button
                    className="dropdown-item text-white"
                    onClick={handleLogout}
                  >
                    <i className="bi bi-box-arrow-right me-2"></i>
                    Sair
                  </button>
                </li>
                
              </ul>
            </div>
          </div>
        </div>
        {/* ChangeAnoBaseModal removed: year control is inline */}
        <PdfConfirmModal
          show={showPdfModal}
          onClose={closePdfModal}
          onConfirm={handleRelatorioWithoutFilter}
          title={pdfModalData?.label}
          isLoading={pdfModalLoading}
        />
        <MesSelectionModal
          show={showMesModal}
          onClose={closeMesModal}
          onConfirm={handleRelatorioMes}
          title={mesModalData?.label}
          isLoading={mesModalLoading}
        />
        <PeriodoSelectionModal
          show={showPeriodoModal}
          onClose={closePeriodoModal}
          onConfirm={handleRelatorioPeriodo}
          title={periodoModalData?.label}
          isLoading={periodoModalLoading}
        />
        <ProgramaPeriodoModal
          show={showProgramaPeriodoModal}
          onClose={closeProgramaPeriodoModal}
          onConfirm={handleRelatorioProgramaPeriodo}
          title={programaPeriodoData?.label}
          isLoading={programaPeriodoModalLoading}
        />
        <DefaultModal {...setDefaultModal} />
        <ChangePasswordModal show={showChangePassword} onClose={() => setShowChangePassword(false)} />
      </div>
      <PdfViewerModal />
    </nav>
  );
}

export default Navbar;
